"use client"

import { Listbox, Transition } from "@headlessui/react"
import { Fragment, useEffect, useMemo, useState } from "react"
import ReactCountryFlag from "react-country-flag"
import { useParams, usePathname } from "next/navigation"

import { updateRegion } from "@lib/data/cart"
import { HttpTypes } from "@medusajs/types"

type CountryOption = {
  country: string | undefined
  region: string
  label: string | undefined
}

type Props = {
  regions: HttpTypes.StoreRegion[]
}

const CountryFlagButton = ({ regions }: Props) => {
  // Use null (not undefined) so Listbox stays controlled from the first
  // render — React warns when value flips from undefined to defined.
  const [current, setCurrent] = useState<CountryOption | null>(null)
  const { countryCode } = useParams()
  const currentPath = usePathname().split(`/${countryCode}`)[1]

  const options = useMemo(() => {
    return regions
      ?.map((r) =>
        r.countries?.map((c) => ({
          country: c.iso_2,
          region: r.id,
          label: c.display_name,
        }))
      )
      .flat()
      .filter((o): o is CountryOption => Boolean(o))
      .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""))
  }, [regions])

  useEffect(() => {
    if (countryCode) {
      const option = options?.find((o) => o.country === countryCode)
      setCurrent(option ?? null)
    }
  }, [options, countryCode])

  const handleChange = (option: CountryOption) => {
    if (!option?.country) return
    updateRegion(option.country, currentPath)
  }

  return (
    <Listbox onChange={handleChange} value={current}>
      <div className="relative">
        <Listbox.Button
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-aov-navy hover:text-aov-orange transition-colors focus:outline-none"
          aria-label="Select shipping country"
          data-testid="country-flag-button"
        >
          {current?.country ? (
            <ReactCountryFlag
              svg
              style={{ width: "22px", height: "22px" }}
              countryCode={current.country}
              aria-label={current.label}
            />
          ) : (
            <span className="text-base leading-none" aria-hidden="true">
              🌐
            </span>
          )}
          <svg
            className="h-3 w-3"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </Listbox.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Listbox.Options
            className="absolute right-0 mt-2 max-h-80 w-64 overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-aov-border z-[60] py-1 focus:outline-none"
            data-testid="country-flag-options"
          >
            {options?.map((o, i) => (
              <Listbox.Option
                key={`${o.country}-${i}`}
                value={o}
                className={({ active, selected }) =>
                  `cursor-pointer select-none py-2 px-3 flex items-center gap-2 text-sm font-body ${
                    active ? "bg-aov-surface text-aov-navy" : "text-aov-navy/80"
                  } ${selected ? "font-semibold text-aov-navy" : ""}`
                }
              >
                <ReactCountryFlag
                  svg
                  style={{ width: "18px", height: "18px" }}
                  countryCode={o.country ?? ""}
                />
                <span>{o.label}</span>
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  )
}

export default CountryFlagButton
